import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Search, X } from 'lucide-react';

export interface LeadsFilterState {
  search: string;
  temperature: string;
  stage: string;
  source: string;
  followUpStatus: string;
}

interface LeadsFiltersProps {
  filters: LeadsFilterState;
  onChange: (filters: LeadsFilterState) => void;
}

const TEMPERATURES = ['all', 'hot', 'warm', 'cold'] as const;
const STAGES = ['all', 'new', 'contacted', 'appointment', 'proposal', 'won', 'lost'] as const;
const SOURCES = ['all', 'manual', 'meta', 'google', 'linkedin', 'website', 'whatsapp', 'other'] as const;
const FOLLOW_UP = ['all', 'overdue', 'today', 'upcoming', 'none'] as const;

export function LeadsFilters({ filters, onChange }: LeadsFiltersProps) {
  const hasFilters = filters.search || 
    filters.temperature !== 'all' || 
    filters.stage !== 'all' || 
    filters.source !== 'all' ||
    filters.followUpStatus !== 'all';

  const clearFilters = () => {
    onChange({
      search: '',
      temperature: 'all',
      stage: 'all',
      source: 'all',
      followUpStatus: 'all',
    });
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search leads..."
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          className="pl-9"
        />
      </div>
      
      <div className="flex flex-wrap gap-2">
        <Select value={filters.temperature} onValueChange={(v) => onChange({ ...filters, temperature: v })}>
          <SelectTrigger className="w-[120px]">
            <SelectValue placeholder="Temperature" />
          </SelectTrigger>
          <SelectContent>
            {TEMPERATURES.map((t) => (
              <SelectItem key={t} value={t} className="capitalize">
                {t === 'all' ? 'All temps' : t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.stage} onValueChange={(v) => onChange({ ...filters, stage: v })}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Stage" />
          </SelectTrigger>
          <SelectContent>
            {STAGES.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s === 'all' ? 'All stages' : s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.source} onValueChange={(v) => onChange({ ...filters, source: v })}>
          <SelectTrigger className="w-[120px] hidden lg:flex">
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent>
            {SOURCES.map((s) => (
              <SelectItem key={s} value={s} className="capitalize">
                {s === 'all' ? 'All sources' : s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filters.followUpStatus} onValueChange={(v) => onChange({ ...filters, followUpStatus: v })}>
          <SelectTrigger className="w-[130px] hidden lg:flex">
            <SelectValue placeholder="Follow-up" />
          </SelectTrigger>
          <SelectContent>
            {FOLLOW_UP.map((f) => (
              <SelectItem key={f} value={f} className="capitalize">
                {f === 'all' ? 'All follow-ups' : f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="w-4 h-4 mr-1" />
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}
